import SelfPayFormDependent from 'src/app/models/selfPayFormDependent';
import { first, cloneDeep, map, filter, some, groupBy, omit, sortBy, find, remove, orderBy, Pairs, toPairs, forEach, every } from 'lodash';
import { faCalendar, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { NgForm } from '@angular/forms';
import { Component, ViewEncapsulation, Input, OnInit, Output, EventEmitter, ViewChild, OnChanges } from '@angular/core';
import SelfPay from 'src/app/models/selfPay';
import Plan from 'src/app/models/plan';
import Agency from 'src/app/models/agency';
import { env } from 'src/env/development';
import Ethnicity from 'src/app/models/ethnicity';
import Carrier from 'src/app/models/carrier';
import * as dayjs from 'dayjs';
import { CoreService } from 'src/app/services/core.service';
import Subscriber from 'src/app/models/subscriber';
import { Lookups, LookupType } from 'src/app/decorators/lookups.decorator';
import PlanType from 'src/app/models/planType';
import SelfPayForm from 'src/app/models/selfPayForm';

@Lookups(LookupType.PlanType)
@Component({
  selector: 'self-pay-elect-medical',
  templateUrl: 'selfPay.electMedical.component.html',
  styleUrls: [],
  providers: [],
  encapsulation: ViewEncapsulation.None
})
export class SelfPayElectMedicalComponent implements OnInit, OnChanges {
  @Output() saveSelfPay: EventEmitter<SelfPay> = new EventEmitter<SelfPay>();
  @Output() previous: EventEmitter<void> = new EventEmitter();
  @ViewChild('electMedicalForm') public electMedicalForm: NgForm;
  @Input() selfPay: SelfPay;
  @Input() availablePlans: Plan[];
  @Input() agency: Agency;
  @Input() ethnicities: Ethnicity[];
  @Input() lookups;
  @Input() subscriber: Subscriber;
  ethnicityDisplayed: boolean = false;
  medicalPlans: Plan[];
  carrierMedicalPlans: Carrier[] = [];
  selectedPlanIds: string[] = [];
  totalPlansAllowed = 1; // only every allow one selection now (even for split accounts -- bug 39383)
  icons = {
      faCalendar,
      faExclamationCircle
  }
  groupedMedicalPlans: {[k:string]: Plan[]};
  splitMedicarePlans = [
    'Kaiser Foundation Health Plan of the Northwest Classic',
    'Kaiser Foundation Health Plan of Washington Classic',
    'Kaiser Foundation Health Plan Washington Sound Choice',
    'Kaiser Foundation Health Plan of Washington Value',
    'Premera Medicare Supplement Plan G',
    'UMP Classic',
    'UnitedHealthcare PEBB Balance',
    'UnitedHealthcare PEBB Complete'
  ];
  fullMedicarePlans = [
      'Kaiser Foundation Health Plan of the Northwest Senior Advantage with Part D',
      'Kaiser Permanente NW Senior Advantage with Part D',
      'Kaiser Foundation Health Plan of Washington Medicare Advantage with Part D',
      'Kaiser Permanente WA Medicare Advantage with Part D',
      'Premera Medicare Supplement Plan G',
      'UMP Classic',
      'UnitedHealthcare PEBB Balance',
      'UnitedHealthcare PEBB Complete'
  ];
  medicarePlanIds = [];
  displayRaceEthnicity = false;
  selectedLanguage = '';
  otherLanguageEnabledSub = false;
  otherLanguageEnabledDep = false;
  isRetiree = false;
  isUnpaid = false;
  selectedHispanicCodesSub = [];
  selectedHispanicCodesDEp = [];
  isCobra: boolean = false;
  noMedicalSelectionMade = false;
  isPebbRetireeOE = false;
  isPebbRetireeSOE = false;
  isPebbRetireeInitial = false;
  isPebbCobraInitial = false;
  isPebbCobraSOE = false;
  isPebbCobraOE = false;
  accountMedicareStatus = null;

  constructor(private coreService: CoreService) {}
  
  ngOnInit() {
    this.isRetiree = this.selfPay?.selfPayType?.selfPayTypeCode === 'R';
    this.isUnpaid = this.selfPay?.selfPayType?.selfPayTypeCode === 'U';
    this.isCobra = env.cobraSelfPayTypeCodes.includes(this.selfPay.selfPayType.selfPayTypeCode);
    this.medicalPlans = cloneDeep(sortBy(filter(this.availablePlans, (ap: Plan) => ap.planType?.planTypeCode === '1' && ap.agencyId === this.agency.agencyId && ap.carrier!=null && ap.planCode!='F' && (ap.planName?.includes('Senior') ? !this.selfPay?.isLWOP : true)),'sortOrder'));
    
    if(env.lwopSelfPayTypeCodes.includes(this.selfPay?.selfPayType?.selfPayTypeCode)) {
      //LWOP
      this.totalPlansAllowed = 1;
      this.medicalPlans = filter(this.medicalPlans, (p: Plan) => !env.medicarePlanCodes.includes(p?.planCode));
    }
    this.medicarePlanIds = map(filter(this.availablePlans, ap => env.medicarePlanCodes.includes(ap.planCode)), 'planId');
    this.noMedicalSelectionMade = !this.selfPay?.formJson?.subscriber?.medicalEnrollInd && !some(this.selfPay?.formJson?.dependents, (d: SelfPayFormDependent) => d.medicalCoverInd);
    this.isPebbRetireeOE = ((this.selfPay?.selfPayType?.selfPayTypeCode === 'R' && dayjs(this.selfPay?.coverageEffectiveDate).isSameOrAfter(new Date('1/1/25'))) || this.selfPay?.selfPayType?.selfPayTypeCode === "OER");
    this.isPebbRetireeInitial = this.selfPay?.isPEBB && this.selfPay.isRetireeInitial;
    this.isPebbCobraInitial = this.selfPay?.isPEBB && this.selfPay.isCobraInitial;
    this.isPebbCobraSOE = this.selfPay?.isPEBB && this.selfPay.isCobraSOE;
    this.isPebbRetireeSOE = this.selfPay?.isPEBB && this.selfPay.isRetireeSOE;
    this.isPebbCobraOE = this.selfPay?.isPEBB && this.selfPay?.selfPayType?.selfPayTypeCode === 'OEC';
    this.accountMedicareStatus = this.getAccountMedicareStatus();
    if(this.isPebbRetireeOE || this.isPebbCobraOE || this.isPebbCobraInitial || this.isPebbCobraSOE || this.isPebbRetireeInitial || this.isPebbRetireeSOE) {
      if(this.accountMedicareStatus === 'N') {
        this.medicalPlans = this.medicalPlans.filter(p => !env.medicarePlanCodes.includes(p.planCode) && !p.planName.includes('Senior'));
      }else if (this.accountMedicareStatus == 'F'){
        this.medicalPlans = this.medicalPlans.filter(p => this.fullMedicarePlans.includes(p.planName));
        this.medicalPlans.forEach(p => {
          p.planName = p.medicareDPlanName ?? p.planName;
          // if(p.planName === 'UMP Classic'){
          //   p.planName = 'UMP Classic Medicare with Part D (PDP)';
          // }
        })
      }else if(this.accountMedicareStatus == 'S') {
        this.medicalPlans = this.medicalPlans.filter(p => this.splitMedicarePlans.includes(p.planName));
        this.medicalPlans.forEach(p => {
          p.planName = p.medicareDPlanName ?? p.planName;
          // if(p.planName === 'UMP Classic'){
          //   p.planName = 'UMP Classic Medicare with Part D (PDP)';
          // }
        })
      }
    }

    // the only medical plan without a carrier is the default placeholder plan. This removes that plan from list.
    this.groupedMedicalPlans = groupBy(this.medicalPlans, 'carrier.carrierName');
    toPairs(this.groupedMedicalPlans).forEach( mp=> {
      var c = new Carrier();
      c.carrierName = mp[0];
      c.plans = mp[1];
      this.carrierMedicalPlans.push(c);
    });
    
    this.carrierMedicalPlans = orderBy(this.carrierMedicalPlans, [mp=> mp.carrierName.toLowerCase()], ["asc"]);

    if (!this.selfPay.formJson.medicalPlans)
      this.selfPay.formJson.medicalPlans = [];
    if (this.selfPay.formJson.medicalPlans.length > 0)
      this.selectedPlanIds = map(this.selfPay.formJson.medicalPlans, 'planId');
    else if (this.selfPay.isPEBB && this.selfPay.isCobraSOE) {
      var enrollments = this.subscriber.enrollments.filter(e =>
        e.plan.planType.planTypeCode === env.medicalPlanTypeCode &&
        e.isActive(this.selfPay.coverageEffectiveDate ?? new Date()) &&
        some(this.medicalPlans, mp => mp.planId === e.planId)
      );

      this.selectedPlanIds = enrollments.map(e => e.planId);
    }
  }

  ngOnChanges(): void {
    if (this.selfPay?.formJson?.dependents[0]) {
      this.selfPay?.formJson.setEthnicityDependents();
    }
  }

  setPlan(planId,srcCheckbox) {
    var seldPlanId = srcCheckbox.srcElement.checked ? planId : null;
    this.selectedPlanIds = [ seldPlanId ];
    this.medicalPlans = [ find(this.availablePlans,(p: Plan) => p.planId == seldPlanId) ];
    this.selfPay.formJson.medicalPlan = this.medicalPlans[0];
    this.selfPay.formJson.medicalPlans = cloneDeep(this.medicalPlans);
    this.selfPay.formJson.continueCurrentSelectedMedical = false;
    // if (this.selectedPlanIds.indexOf(planId) > -1)
    // {
    //   //removing selected plan
    //   remove(this.selfPay.formJson.medicalPlans,(p)=>p.planId == planId);
    //   remove(this.selectedPlanIds,(p)=>p == planId);
    // }
    // else {
    //   //adding new plan, allow only 2 max plans selected for COBRA/Retiree
    //   if (this.selectedPlanIds.length>this.totalPlansAllowed-1) {
    //     this.selectedPlanIds.splice(0,1);
    //     this.selfPay.formJson.medicalPlans.splice(0,1);
    //   }
    //   this.selectedPlanIds.push(planId);
    //   this.selfPay.formJson.medicalPlans.push(find(this.availablePlans,(p: Plan) => p.planId == planId));
    // }

    // if (this.totalPlansAllowed==1) {
    //   //set single selected medical plan to the first in the list if it exists
    //   if (this.selfPay.formJson.medicalPlans.length>0) {
    //     this.selfPay.formJson.medicalPlan = this.selfPay.formJson.medicalPlans[0];
    //   }
    //   else {
    //     this.selfPay.formJson.medicalPlan = null;
    //   }

    //   this.selfPay.formJson.continueCurrentSelectedMedical = false;
    // }
  }

  emitSelfPay(e) {
    this.saveSelfPay.emit(e);
  }

  subscriberOrDependentsAreMedicareEnrolled(): boolean {
    return this.selfPay.formJson?.subscriber?.medicarePartAInd || some(this.selfPay.formJson?.dependents, (d: SelfPayFormDependent) => d?.medicarePartAInd);
  }

  continueOrDisplayRE(selfPay) {
    if (!this.noMedicalSelectionMade) {
      if (this.selfPay.isRetiree || (this.selfPay.isPEBB && this.selfPay.isCobraOE)) {
        if (!this.selectedPlanIds.length && !this.selfPay?.formJson?.continueCurrentSelectedMedical) {
          this.coreService.popMessage('A single plan must be selected', 'error', 5000);
          return;
        }
      }
    }

    if(!this.ethnicityDisplayed &&  
      (some(env.selfPayRetireeRequestCodes,(cd)=> cd == this.selfPay.selfPayType?.selfPayTypeCode) || (this.selfPay.isPEBB && some(env.cobraSelfPayTypeCodes,(cd)=> cd == this.selfPay.selfPayType?.selfPayTypeCode))) && 
      this.subscriberOrDependentsAreMedicareEnrolled() && some(this.lookups?.applicationSetting, (as) => as.applicationSettingCode === 'MAPD' && as.response?.responseCode === 'Y')) {
      this.displayRaceEthnicity = true;
      this.ethnicityDisplayed = true;
      this.scrollToTop();
    } else {
      if (this.selfPay?.formJson?.continueCurrentSelectedVision) {
        const medicalPlanTypeId = first(this.lookups.planTypes.filter((pt: PlanType) => pt.planTypeCode === env.medicalPlanTypeCode)).planTypeId;
        this.selfPay.formJson.medicalPlan = first(orderBy(this.subscriber.enrollments.filter(e => e.planTypeId === medicalPlanTypeId), 'effectiveStartDate', 'desc'))?.plan;
      }
      this.ethnicityDisplayed = false;
      this.saveSelfPay.emit(selfPay);
    }
  }

  emitPrevious() {
    this.ethnicityDisplayed = false;
    this.previous.emit();
  }

  updateFormJsonSub(e) {
    this.selfPay.formJson.subscriber.memberEthnicities = e;
  }

  updateFormJsonSpouse(e) {
    this.selfPay.formJson.ethnicitySpouse.memberEthnicities = e;
  }

  updateFormJsonDependent(e) {
    this.selfPay.formJson.ethnicityDependent.memberEthnicities = e;
  }

  continueCurrentSelected(checked) {
    if(checked) {
      this.selfPay.formJson.continueCurrentSelectedVision = true;
      this.selectedPlanIds = [];
    }
  }

  getAccountMedicareStatus(): string {
    const membersWithMedical = this.selfPay?.formJson?.dependents.filter(d => d.medicalCoverInd).map(d => d?.medicarePartBInd ?? d?.memberMedicare?.medicarePartBEnrolledInd);
    if (this.selfPay?.formJson?.subscriber.medicalEnrollInd)
      membersWithMedical.push(this.selfPay.formJson.subscriber?.medicarePartBInd ?? this.selfPay.formJson.subscriber?.memberMedicare?.medicarePartBEnrolledInd);

    if (every(membersWithMedical, hasMedB => hasMedB)) {
      return 'F';
    } else if (some(membersWithMedical, hasMedB => hasMedB)) {
      return 'S'
    } else if (!every(membersWithMedical, hasMedB => hasMedB)) {
      return 'N';
    }
  }

  previousFromEthnicity() {
    this.displayRaceEthnicity = false;
    this.ethnicityDisplayed = false;
    this.scrollToTop();
  }

  scrollToTop(): void {
    setTimeout(() => {
      const h = window.document.querySelector('self-pay h2, self-pay h3');
      if (h) h.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  }

}
